/**
 * Turn Color Rendering Utilities
 * 
 * SINGLE SOURCE OF TRUTH for Turn Color rendering.
 * All components MUST use these utilities - no inline rendering logic.
 * 
 * Key principle: PRESERVE ORIGINAL CASE
 * - Turn codes contain lowercase letters (e.g., [i-lime])
 * - Original word may have uppercase (e.g., "I")
 * - We map original characters to turn code colors, preserving case
 */

import React from 'react';
import { parseTurnCode, getHexColor, TurnColorSegment, TurnColorMapping, STANDARD_COLORS } from './parser';

export interface TurnCodeError {
  word: string;
  turnCode: string;
  error: string;
  extractedLetters: string;
}

export interface TurnColorWordProps {
  /** Original word from text (preserves case) */
  word: string;
  /** Turn code from lexicon (e.g., "[i-lime]") */
  turnCode: string;
  /** Color mappings from database or STANDARD_COLORS */
  colorMappings?: TurnColorMapping[];
  /** Rendering phase: 1 = colored squares, 2 = colored underlines */
  phase?: 1 | 2;
  /** Optional className for the container */
  className?: string;
  /** Callback when turn_code validation fails - allows parent to handle/fix */
  onError?: (error: TurnCodeError) => void;
}

export interface TurnColorSegmentWithOriginal extends TurnColorSegment {
  /** Original characters from the word (preserving case) */
  originalChars: string;
}

/**
 * Validate that a turn code matches the word structure.
 * Returns validation result with extracted letters for debugging.
 * 
 * This is the RENDER-TIME validation that catches data corruption before
 * it causes rendering bugs like "factt" from a malformed turn_code.
 */
function validateTurnCodeForWord(word: string, turnCode: string): { 
  valid: boolean; 
  extractedLetters: string;
  error?: string;
} {
  const segments = parseTurnCode(turnCode);
  let extractedLetters = '';
  
  for (const seg of segments) {
    extractedLetters += seg.content;
  }
  
  if (extractedLetters.length !== word.length) {
    return {
      valid: false,
      extractedLetters,
      error: `Length mismatch: word "${word}" has ${word.length} chars, turn_code has ${extractedLetters.length}`,
    };
  }
  
  if (extractedLetters.toLowerCase() !== word.toLowerCase()) {
    return {
      valid: false,
      extractedLetters,
      error: `Letter mismatch: word "${word}" vs turn_code "${extractedLetters}"`,
    };
  }
  
  return { valid: true, extractedLetters };
}

/**
 * Map turn code segments to original word characters.
 * This is the core DRY utility - preserves original case while applying turn code colors.
 * 
 * VALIDATION: If turn_code doesn't match word, logs warning and renders word as plain text.
 * This prevents rendering bugs like "factt" from corrupted turn_code data.
 * 
 * @param word - Original word from text (e.g., "I", "Buffalo")
 * @param turnCode - Turn code from lexicon (e.g., "[i-lime]", "b, [u-plum], ff, [a-black], l, [o-yellow]")
 * @returns Segments with originalChars preserving case from the word, or null if invalid
 */
export function mapTurnCodeToWord(word: string, turnCode: string): TurnColorSegmentWithOriginal[] {
  const segments = parseTurnCode(turnCode);
  let charIndex = 0;
  
  return segments.map(seg => {
    const segLength = seg.content.length;
    const originalChars = word.slice(charIndex, charIndex + segLength);
    charIndex += segLength;
    
    return {
      ...seg,
      originalChars: originalChars || seg.content, // Fallback if word shorter (shouldn't happen after validation)
    };
  });
}

/**
 * Render a Turn Color word with proper case preservation.
 * Use this instead of inline rendering logic.
 * 
 * @example
 * // Phase 2 (underlines) - default
 * <TurnColorWord word="I" turnCode="[i-lime]" />
 * 
 * // Phase 1 (squares)
 * <TurnColorWord word="cat" turnCode="c, [a-black], t" phase={1} />
 */
export const TurnColorWord: React.FC<TurnColorWordProps> = ({
  word,
  turnCode,
  colorMappings = STANDARD_COLORS,
  phase = 2,
  className = '',
  onError,
}) => {
  // Validate before rendering
  const validation = validateTurnCodeForWord(word, turnCode);
  
  // If validation failed, render with error indicator (clickable to fix)
  if (!validation.valid) {
    const errorData: TurnCodeError = {
      word,
      turnCode,
      error: validation.error || 'Unknown validation error',
      extractedLetters: validation.extractedLetters,
    };
    
    // Render with red underline - click to trigger fix
    return (
      <span 
        className={`inline border-b-2 border-dashed border-red-500 cursor-pointer hover:bg-red-500/10 transition-colors ${className}`}
        title={`Turn Code Error: ${validation.error}\nClick to fix`}
        onClick={() => onError?.(errorData)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onError?.(errorData)}
      >
        {word}
      </span>
    );
  }
  
  const segments = mapTurnCodeToWord(word, turnCode);
  
  return (
    <span className={`inline ${className}`}>
      {segments.map((seg, idx) => {
        // Text segments (consonants) - plain black text
        if (seg.type === 'text') {
          return <span key={idx} className="text-gray-900">{seg.originalChars}</span>;
        }
        
        // Color segments (vowels/phonemes)
        const hexColor = getHexColor(seg.color || '', colorMappings);
        
        if (phase === 1) {
          // Phase 1: Colored squares replacing the letter
          return (
            <span
              key={idx}
              className="inline-block w-5 h-5 rounded-sm mx-px align-middle"
              style={{ backgroundColor: hexColor }}
              title={seg.originalChars}
            />
          );
        } else {
          // Phase 2: Letters with colored pill-shaped underline.
          // Inline-block span with ::after pseudo-element (via CSS class) draws
          // a rounded bar beneath the text. Color passed as CSS custom property.
          return (
            <span
              key={idx}
              className="turn-color-underline"
              style={{ '--tc-color': hexColor } as React.CSSProperties}
            >
              {seg.originalChars}
            </span>
          );
        }
      })}
    </span>
  );
};

/**
 * Render a sentence with Turn Color words.
 * Handles word lookup and renders each word with appropriate styling.
 */
export interface TurnColorSentenceProps {
  /** Array of words with their turn codes */
  words: Array<{
    word: string;
    turnCode: string | null;
    inLexicon: boolean;
  }>;
  /** Color mappings from database */
  colorMappings?: TurnColorMapping[];
  /** Rendering phase */
  phase?: 1 | 2;
  /** Container className */
  className?: string;
  /** Style for the container */
  style?: React.CSSProperties;
  /** Callback when a turn_code validation fails - allows parent to handle/fix */
  onTurnCodeError?: (error: TurnCodeError) => void;
}

/**
 * Extract the alphabetic core of a word and surrounding punctuation.
 * E.g., '"I' -> { leading: '"', core: 'I', trailing: '' }
 * E.g., 'Ram!"' -> { leading: '', core: 'Ram', trailing: '!"' }
 */
function extractWordParts(word: string): { leading: string; core: string; trailing: string } {
  // Match: (leading punctuation)(alphabetic core with apostrophes/hyphens)(trailing punctuation)
  const match = word.match(/^([^a-zA-Z'-]*)([a-zA-Z'-]+)([^a-zA-Z'-]*)$/);
  if (match) {
    return { leading: match[1], core: match[2], trailing: match[3] };
  }
  // No alphabetic content - return whole word as-is
  return { leading: '', core: word, trailing: '' };
}

export const TurnColorSentence: React.FC<TurnColorSentenceProps> = ({
  words,
  colorMappings = STANDARD_COLORS,
  phase = 2,
  className = '',
  style = {},
  onTurnCodeError,
}) => {
  const isPunctuationOnly = (w: string) => /^[^a-zA-Z']+$/.test(w);

  return (
    <div className={`turn-color-text ${className}`} style={{ wordSpacing: '0.35em', ...style }}>
      {words.map((wordData, idx) => {
        const { word, turnCode, inLexicon } = wordData;
        const nextWord = words[idx + 1];
        const suppressSpace = nextWord && isPunctuationOnly(nextWord.word);
        
        // Non-Turn-Color word - plain text
        if (!inLexicon || !turnCode) {
          return <span key={idx}>{word}{suppressSpace ? '' : ' '}</span>;
        }
        
        // Extract punctuation from word (e.g., '"I' -> leading='"', core='I')
        const { leading, core, trailing } = extractWordParts(word);
        
        // Turn Color word with punctuation preserved
        return (
          <span key={idx}>
            {leading}
            <TurnColorWord
              word={core}
              turnCode={turnCode}
              colorMappings={colorMappings}
              phase={phase}
              onError={onTurnCodeError}
            />
            {trailing}
            {suppressSpace ? '' : ' '}
          </span>
        );
      })}
    </div>
  );
};

export default TurnColorWord;
